import { openDB, type IDBPDatabase } from 'idb';
import { emptySave, migrate, type SaveData } from '../core/save/model.js';

/**
 * Persistence. Three stores:
 *
 *   meta   'save' -> SaveData, 'save.backup' -> the previous SaveData
 *   thumbs carId  -> baked PNG Blob (the car sprite and its garage thumbnail)
 *
 * The rolling backup is the whole point of this module. Browser storage on iOS can be evicted, a
 * force-quit mid-write can truncate a record, and there is no way to apologise to a three-year-old
 * for losing her cars - so every write demotes the current save to the backup slot first, and a
 * corrupt read falls through to it.
 */

const DB_NAME = 'pawp';
const DB_VERSION = 1;
const STORE_META = 'meta';
const STORE_THUMBS = 'thumbs';
const KEY_SAVE = 'save';
const KEY_BACKUP = 'save.backup';

let dbPromise: Promise<IDBPDatabase> | null = null;

function db(): Promise<IDBPDatabase> {
  dbPromise ??= openDB(DB_NAME, DB_VERSION, {
    upgrade(database) {
      if (!database.objectStoreNames.contains(STORE_META)) database.createObjectStore(STORE_META);
      if (!database.objectStoreNames.contains(STORE_THUMBS)) {
        database.createObjectStore(STORE_THUMBS);
      }
    },
  });
  return dbPromise;
}

/**
 * Load the save, falling back to the backup and then to a fresh save. Never rejects: a boot that
 * fails because storage is unhappy would be indistinguishable from a broken game.
 */
export async function loadSave(): Promise<SaveData> {
  try {
    const store = await db();
    const primary = await store.get(STORE_META, KEY_SAVE);
    if (primary !== undefined) return migrate(primary);

    const backup = await store.get(STORE_META, KEY_BACKUP);
    if (backup !== undefined) {
      console.warn('[pawp] primary save missing, recovered from backup');
      return migrate(backup);
    }
  } catch (error) {
    console.error('[pawp] could not read save, starting fresh', error);
  }
  return emptySave();
}

/** Write the save, demoting the previous one to the backup slot in the same transaction. */
export async function writeSave(data: SaveData): Promise<void> {
  const store = await db();
  const tx = store.transaction(STORE_META, 'readwrite');
  const meta = tx.objectStore(STORE_META);
  const previous = await meta.get(KEY_SAVE);
  if (previous !== undefined) await meta.put(previous, KEY_BACKUP);
  await meta.put(structuredClone(data), KEY_SAVE);
  await tx.done;
}

export async function putCarImage(carId: string, png: Blob): Promise<void> {
  const store = await db();
  await store.put(STORE_THUMBS, png, carId);
}

export async function getCarImage(carId: string): Promise<Blob | undefined> {
  const store = await db();
  return store.get(STORE_THUMBS, carId);
}

export async function deleteCarImage(carId: string): Promise<void> {
  const store = await db();
  await store.delete(STORE_THUMBS, carId);
}

/**
 * Everything, gone: the save, its backup, and every thumbnail.
 *
 * Behind the parent gate and behind a second press (`parent-screen.ts`), because this is the one button in
 * the build that can take every car at once. The whole database goes rather than the save being emptied:
 * the thumbnails are in their own store, and a "delete everything" that leaves a dozen PNGs of her cars
 * behind is not what it says it is.
 *
 * The connection is closed and the cached promise dropped first - `deleteDatabase` blocks indefinitely
 * while anything still holds the database open, and a delete that silently never completes is worse than
 * one that fails. The caller reloads immediately afterwards, which is also what gives the app a fresh save.
 */
export async function deleteEverything(): Promise<void> {
  const open = dbPromise;
  dbPromise = null;
  try {
    (await open)?.close();
  } catch {
    // Already closed, or never opened. Either way there is nothing holding it now.
  }

  await new Promise<void>((resolve, reject) => {
    const request = indexedDB.deleteDatabase(DB_NAME);
    request.onsuccess = () => resolve();
    request.onblocked = () => resolve(); // Another tab has it open; it will go when that tab does.
    request.onerror = () => reject(request.error ?? new Error('could not delete the save'));
  });
}

/**
 * Everything, for the parent-gated "save a copy" button. Cars are the only irreplaceable thing in
 * this game; being able to get them out of browser storage and onto a real filesystem is the last
 * line of defence.
 */
export async function exportAll(): Promise<{ save: SaveData; images: Map<string, Blob> }> {
  const save = await loadSave();
  const images = new Map<string, Blob>();
  for (const car of save.garage) {
    const png = await getCarImage(car.id);
    if (png !== undefined) images.set(car.id, png);
  }
  return { save, images };
}
